import { create } from 'zustand';
import { ApiError, NetworkError, api } from '../lib/api';
import * as cache from '../lib/db';
import { BASE_LAYOUT, assumedAccess, canSee } from '../lib/permissions';
import type {
  ChecklistItem,
  Field,
  Group,
  GroupRole,
  Member,
  MemberAccess,
  MemberLayout,
  OutboxOp,
  Priority,
  SyncState,
  Task,
  TaskStatus,
  User,
  Version,
} from '../lib/types';

/**
 * Only the team to fall back on when someone opens the bare origin. Which team
 * is on screen comes from the URL, so two tabs can hold two different boards.
 */
export const LAST_GROUP_KEY = 'pw.activeGroup';
const HEARTBEAT_MS = 20_000;
const MAX_BACKOFF_MS = 30_000;

type TaskInput = {
  title: string;
  description?: string;
  fieldId: string | null;
  status?: TaskStatus;
  priority?: Priority;
  checklist?: ChecklistItem[];
  /**
   * Left out, the field's own default assignee applies — the server decides, and
   * the optimistic row below mirrors that choice so the card does not change
   * owner a moment after it appears.
   */
  assigneeId?: string | null;
};

type TaskPatch = Partial<{
  title: string;
  description: string;
  fieldId: string | null;
  status: TaskStatus;
  priority: Priority;
  checklist: ChecklistItem[];
  assigneeId: string | null;
  position: number;
}>;

type Store = {
  user: User | null;
  ready: boolean;

  groups: Group[];
  activeGroupId: string | null;
  role: GroupRole | null;
  /** What this person may see and do in the team on screen. */
  access: MemberAccess | null;
  /** The view they open it on: the admin's starting point plus their own changes. */
  layout: MemberLayout;
  /** False while they are still on the layout the admin gave them. */
  hasOwnLayout: boolean;

  tasks: Task[];
  fields: Field[];
  versions: Version[];
  members: Member[];

  syncState: SyncState;
  /** Unsent operations for the team on screen. */
  pendingCount: number;
  /** Unsent operations belonging to the user's other teams. */
  pendingOtherCount: number;
  /** Tasks with an unsent local change, so the board can mark them. */
  pendingTaskIds: string[];
  lastSyncedAt: number | null;
  syncError: string | null;

  bootstrap: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  register: (input: {
    email: string;
    password: string;
    displayName: string;
    inviteToken?: string;
  }) => Promise<string | null>;
  logout: () => Promise<void>;

  loadGroups: () => Promise<Group[]>;
  setActiveGroup: (groupId: string) => Promise<void>;
  createGroup: (name: string) => Promise<Group>;
  joinGroup: (code: string) => Promise<Group>;

  createTask: (input: TaskInput) => Promise<void>;
  updateTask: (taskId: string, patch: TaskPatch) => Promise<void>;
  deleteTask: (taskId: string) => Promise<void>;
  /** The tick on a card: closes the task, or reopens it into To do. */
  toggleDone: (taskId: string) => Promise<void>;
  /** The claim on a card: takes the task, or hands it back if already mine. */
  toggleClaim: (taskId: string) => Promise<void>;
  /** Drops a card into a column at `position`, spreading the column if needed. */
  moveTask: (
    taskId: string,
    status: TaskStatus,
    placement: { position: number; spread: Array<{ id: string; position: number }> },
  ) => Promise<void>;

  /**
   * Saves part of the member's own arrangement. Applied locally first so the
   * board responds to a filter change without waiting on the network.
   */
  saveLayout: (patch: Partial<MemberLayout>) => Promise<void>;
  /** Hands the view back to whatever the admin set up. */
  resetLayout: () => Promise<void>;

  refresh: () => Promise<void>;
  flush: () => Promise<void>;
  reloadFromCache: () => Promise<void>;
};

let heartbeatTimer: number | undefined;
let retryTimer: number | undefined;
let flushing = false;

export const useStore = create<Store>((set, get) => {
  /**
   * Mirrors the outbox into state so the UI can show unsynced work. Split by
   * team: the board must not count, or mark, work queued for a team that is not
   * on screen, but the total still has to be visible somewhere — unsent changes
   * in a team you are not looking at are exactly what a user would otherwise
   * lose track of.
   */
  async function syncPendingCount(): Promise<void> {
    const ops = await cache.listOutbox();
    const activeGroupId = get().activeGroupId;
    const mine = ops.filter((op) => op.groupId === activeGroupId);
    set({
      pendingCount: mine.length,
      pendingOtherCount: ops.length - mine.length,
      pendingTaskIds: mine.map((op) => op.taskId),
    });
  }

  function scheduleRetry(attempts: number): void {
    window.clearTimeout(retryTimer);
    const delay = Math.min(1000 * 2 ** attempts, MAX_BACKOFF_MS);
    retryTimer = window.setTimeout(() => void get().refresh(), delay);
  }

  /** Writes a task to both the local cache and the in-memory list. */
  async function commitLocalTask(groupId: string, task: Task): Promise<void> {
    await cache.putLocalTask(groupId, task);
    set((state) => {
      const index = state.tasks.findIndex((t) => t.id === task.id);
      if (index === -1) return { tasks: [...state.tasks, task] };
      const tasks = state.tasks.slice();
      tasks[index] = task;
      return { tasks };
    });
  }

  return {
    user: null,
    ready: false,
    groups: [],
    activeGroupId: null,
    role: null,
    access: null,
    layout: BASE_LAYOUT,
    hasOwnLayout: false,
    tasks: [],
    fields: [],
    versions: [],
    members: [],
    syncState: 'idle',
    pendingCount: 0,
    pendingOtherCount: 0,
    pendingTaskIds: [],
    lastSyncedAt: null,
    syncError: null,

    async bootstrap() {
      // Paint from cache before the network is consulted, so a cold start on a
      // flaky connection still shows the board immediately.
      const cachedUser = await cache.readMeta<User>('user');
      if (cachedUser) set({ user: cachedUser });

      try {
        const { user } = await api<{ user: User }>('/api/auth/me');
        set({ user, syncState: 'idle' });
        await cache.writeMeta('user', user);
        await get().loadGroups();
      } catch (error) {
        if (error instanceof NetworkError && cachedUser) {
          // Offline with a known session: run from the cache. The route still
          // decides which team opens, so only the list is restored here.
          set({ syncState: 'offline' });
          set({ groups: (await cache.readMeta<Group[]>('groups')) ?? [] });
        } else {
          set({ user: null });
          await cache.writeMeta('user', undefined);
        }
      } finally {
        set({ ready: true });
        await syncPendingCount();
        startHeartbeat();
      }
    },

    async login(email, password) {
      const { user } = await api<{ user: User }>('/api/auth/login', {
        method: 'POST',
        body: { email, password },
      });
      set({ user, syncError: null });
      await cache.writeMeta('user', user);
      await get().loadGroups();
      startHeartbeat();
    },

    async register(input) {
      const { user, groupId } = await api<{ user: User; groupId: string | null }>(
        '/api/auth/register',
        {
        method: 'POST',
        body: input,
        },
      );
      await cache.writeMeta('user', user);
      await get().loadGroups();
      set({ user, syncError: null });
      startHeartbeat();
      return groupId;
    },

    async logout() {
      stopHeartbeat();
      await api('/api/auth/logout', { method: 'POST' }).catch(() => undefined);
      await cache.clearAll();
      localStorage.removeItem(LAST_GROUP_KEY);
      set({
        user: null,
        groups: [],
        activeGroupId: null,
        role: null,
        access: null,
        layout: BASE_LAYOUT,
        hasOwnLayout: false,
        tasks: [],
        fields: [],
        versions: [],
        members: [],
        pendingCount: 0,
        pendingOtherCount: 0,
        pendingTaskIds: [],
        syncState: 'idle',
        syncError: null,
      });
    },

    /**
     * Refreshes the list of memberships only — picking a team is the router's
     * job. Also the place where a membership that has gone away (removed from
     * the team, or the team deleted) is noticed: the active team is dropped, and
     * the route then resolves to nothing and sends the user elsewhere.
     */
    async loadGroups() {
      const { groups } = await api<{ groups: Group[] }>('/api/groups');
      await cache.writeMeta('groups', groups);

      const active = groups.find((group) => group.id === get().activeGroupId);
      if (active) {
        set({
          groups,
          role: active.role,
          access: active.access ?? assumedAccess(active.role),
          ...(active.layout ? { layout: active.layout } : {}),
        });
      } else {
        set({
          groups,
          activeGroupId: null,
          role: null,
          access: null,
          layout: BASE_LAYOUT,
          hasOwnLayout: false,
          tasks: [],
          fields: [],
          versions: [],
          members: [],
        });
      }
      await syncPendingCount();
      return groups;
    },

    /**
     * Points the store at the team the URL names. Called from the route, so the
     * active team is per-tab and survives a reload, and the browser's back button
     * walks back through team switches like any other navigation.
     */
    async setActiveGroup(groupId) {
      const group = get().groups.find((g) => g.id === groupId);
      if (!group) return;

      // Where `/` sends this browser next time.
      localStorage.setItem(LAST_GROUP_KEY, groupId);

      // Navigating between pages of the same team must not re-enter the board.
      if (get().activeGroupId === groupId) {
        if (get().role !== group.role) set({ role: group.role });
        return;
      }

      set({
        activeGroupId: groupId,
        role: group.role,
        // From the list for now; the first delta replaces it with the server's
        // own answer, and `assumedAccess` only covers the gap until then.
        access: group.access ?? assumedAccess(group.role),
        layout: group.layout ?? BASE_LAYOUT,
        hasOwnLayout: false,
        tasks: [],
        fields: [],
        versions: [],
        members: [],
        lastSyncedAt: null,
        syncError: null,
      });
      await syncPendingCount();
      await get().reloadFromCache();
      await get().refresh();
    },

    /** Returns the new team so the caller can navigate to it. */
    async createGroup(name) {
      const { group } = await api<{ group: Group }>('/api/groups', {
        method: 'POST',
        body: { name },
      });
      await get().loadGroups();
      return group;
    },

    /** Redeems a join code and returns the team it belongs to. */
    async joinGroup(code) {
      const { group } = await api<{ group: Group; alreadyMember: boolean }>('/api/groups/join', {
        method: 'POST',
        body: { code },
      });
      await get().loadGroups();
      return group;
    },

    async reloadFromCache() {
      const groupId = get().activeGroupId;
      if (!groupId) return;
      const snapshot = await cache.readSnapshot(groupId);
      set({
        tasks: snapshot.tasks,
        fields: snapshot.fields.sort((a, b) => a.position - b.position),
        versions: snapshot.versions,
        members: snapshot.members,
      });
    },

    async createTask(input) {
      const groupId = get().activeGroupId;
      const user = get().user;
      if (!groupId || !user) return;

      const now = new Date().toISOString();
      const taskId = crypto.randomUUID();
      const checklist = input.checklist ?? [];

      // The same rule the server applies, so the optimistic card already shows the
      // person the field routes its work to.
      const assigneeId =
        input.assigneeId !== undefined
          ? input.assigneeId
          : (get().fields.find((f) => f.id === input.fieldId)?.defaultAssigneeId ?? null);

      // Optimistic row. `rev: 0` marks it as never having round-tripped.
      const optimistic: Task = {
        id: taskId,
        fieldId: input.fieldId,
        versionId: null,
        title: input.title,
        description: input.description ?? '',
        status: input.status ?? 'todo',
        priority: input.priority ?? 'medium',
        checklist,
        assigneeId,
        createdBy: user.id,
        completedBy: input.status === 'done' ? user.id : null,
        completedAt: input.status === 'done' ? now : null,
        // Below everything already on the board, matching the server's max + 1.
        position: Math.max(0, ...get().tasks.map((t) => t.position)) + 1,
        createdAt: now,
        updatedAt: now,
        deletedAt: null,
        rev: 0,
      };

      await commitLocalTask(groupId, optimistic);
      await cache.enqueue({
        opId: crypto.randomUUID(),
        kind: 'task.create',
        groupId,
        taskId,
        payload: {
          id: taskId,
          fieldId: input.fieldId,
          title: input.title,
          description: input.description ?? '',
          status: input.status ?? 'todo',
          priority: input.priority ?? 'medium',
          checklist,
          // Omitted rather than resolved, so the field's default is applied by the
          // server. Sending the resolved id would make a member look like they had
          // assigned a colleague, which only admins may do.
          ...(input.assigneeId !== undefined ? { assigneeId: input.assigneeId } : {}),
        },
        attempts: 0,
        queuedAt: Date.now(),
      });

      await syncPendingCount();
      void get().flush();
    },

    async updateTask(taskId, patch) {
      const groupId = get().activeGroupId;
      const user = get().user;
      const current = get().tasks.find((t) => t.id === taskId);
      if (!groupId || !user || !current) return;

      const now = new Date().toISOString();
      const becameDone = patch.status === 'done' && current.status !== 'done';
      const leftDone = patch.status !== undefined && patch.status !== 'done';

      const next: Task = {
        ...current,
        ...patch,
        completedAt: becameDone ? now : leftDone ? null : current.completedAt,
        completedBy: becameDone ? user.id : leftDone ? null : current.completedBy,
        updatedAt: now,
      };

      await commitLocalTask(groupId, next);

      // An unsent create already carries the full task, so fold the edit into it
      // rather than queueing an update for a row the server has not seen.
      const pendingCreate = (await cache.listOutbox()).find(
        (op) => op.taskId === taskId && op.kind === 'task.create',
      );
      if (pendingCreate) {
        await cache.updateOp({
          ...pendingCreate,
          payload: { ...pendingCreate.payload, ...patch },
        });
      } else {
        const pendingUpdate = await cache.findPendingUpdate(taskId);
        if (pendingUpdate) {
          await cache.updateOp({
            ...pendingUpdate,
            payload: { ...pendingUpdate.payload, ...patch },
            attempts: 0,
          });
        } else {
          await cache.enqueue({
            opId: crypto.randomUUID(),
            kind: 'task.update',
            groupId,
            taskId,
            payload: { ...patch },
            attempts: 0,
            queuedAt: Date.now(),
          });
        }
      }

      await syncPendingCount();
      void get().flush();
    },

    async deleteTask(taskId) {
      const groupId = get().activeGroupId;
      if (!groupId) return;

      await cache.removeLocalTask(taskId);
      set((state) => ({ tasks: state.tasks.filter((t) => t.id !== taskId) }));

      // If the create never left this device, the whole task can just vanish.
      const ops = await cache.listOutbox();
      const pendingCreate = ops.find((op) => op.taskId === taskId && op.kind === 'task.create');
      for (const op of ops.filter((o) => o.taskId === taskId)) {
        await cache.dequeue(op.opId);
      }

      if (!pendingCreate) {
        await cache.enqueue({
          opId: crypto.randomUUID(),
          kind: 'task.delete',
          groupId,
          taskId,
          payload: {},
          attempts: 0,
          queuedAt: Date.now(),
        });
      }

      await syncPendingCount();
      void get().flush();
    },

    /**
     * The tick on a card. Reopening sends the task back to To do rather than to
     * wherever it came from: the column it was in before being closed is not
     * recorded anywhere, and To do is the honest answer.
     */
    async toggleDone(taskId) {
      const current = get().tasks.find((t) => t.id === taskId);
      if (!current) return;
      await get().updateTask(taskId, { status: current.status === 'done' ? 'todo' : 'done' });
    },

    /** One click takes a task; clicking again on your own task hands it back. */
    async toggleClaim(taskId) {
      const user = get().user;
      const current = get().tasks.find((t) => t.id === taskId);
      if (!user || !current) return;
      await get().updateTask(taskId, {
        assigneeId: current.assigneeId === user.id ? null : user.id,
      });
    },

    /**
     * A drop. The moved card is written last so that if the spread is interrupted
     * — a dropped connection mid-flush — the board is never left with the card in
     * its new slot and its neighbours still numbered the old way.
     */
    async moveTask(taskId, status, placement) {
      for (const row of placement.spread) {
        if (row.id === taskId) continue;
        await get().updateTask(row.id, { position: row.position });
      }
      await get().updateTask(taskId, { status, position: placement.position });
    },

    /**
     * Drains the outbox in order. A network failure stops the drain and leaves
     * the remaining operations queued; a rejection from the server discards the
     * operation, because replaying it would fail identically.
     */
    async flush() {
      if (flushing) return;

      // Every operation carries its own team, so the whole queue drains no matter
      // which board is open — otherwise a change made in one team would sit
      // unsent for as long as the user is looking at another.
      const ops = await cache.listOutbox();
      if (ops.length === 0) return;

      flushing = true;
      set({ syncState: 'syncing' });

      try {
        for (const op of ops) {
          try {
            const task = await sendOp(op);
            await cache.dequeue(op.opId);
            // Cached under its own team; only merged into the visible list when
            // that team is the one on screen.
            if (task) {
              if (op.groupId === get().activeGroupId) await commitLocalTask(op.groupId, task);
              else await cache.putLocalTask(op.groupId, task);
            }
          } catch (error) {
            if (error instanceof NetworkError) {
              set({ syncState: 'offline' });
              scheduleRetry(op.attempts);
              return;
            }

            if (error instanceof ApiError && error.isPermanent) {
              await cache.dequeue(op.opId);
              set({
                syncState: 'error',
                syncError: `A change to "${op.kind.split('.')[1]}" was rejected: ${error.message}`,
              });
              continue;
            }

            // 5xx or 429: keep it queued and back off.
            await cache.updateOp({ ...op, attempts: op.attempts + 1 });
            scheduleRetry(op.attempts + 1);
            return;
          }
        }

        set({ syncState: 'idle' });
      } finally {
        flushing = false;
        await syncPendingCount();
      }
    },

    /**
     * The member's own half of the layout. Written locally first so the board
     * reacts at once, then sent — and the server's answer is taken as final,
     * since it clamps the layout to what this person is actually allowed to open.
     */
    async saveLayout(patch) {
      const groupId = get().activeGroupId;
      if (!groupId) return;

      set({ layout: { ...get().layout, ...patch }, hasOwnLayout: true });
      try {
        const result = await api<{ layout: MemberLayout; hasOwnLayout: boolean }>(
          `/api/me/groups/${groupId}/layout`,
          { method: 'PATCH', body: patch },
        );
        set({ layout: result.layout, hasOwnLayout: result.hasOwnLayout });
      } catch {
        // A view preference is not worth an error banner: the local change stands
        // for this session and the next delta restores the stored truth.
      }
    },

    async resetLayout() {
      const groupId = get().activeGroupId;
      if (!groupId) return;
      const result = await api<{ layout: MemberLayout; hasOwnLayout: boolean }>(
        `/api/me/groups/${groupId}/layout`,
        { method: 'PATCH', body: { reset: true } },
      );
      set({ layout: result.layout, hasOwnLayout: result.hasOwnLayout });
    },

    /** Sends anything queued, then pulls the server delta on top of it. */
    async refresh() {
      const groupId = get().activeGroupId;
      if (!groupId) return;

      await get().flush();
      if (get().syncState === 'offline') return;

      try {
        const { rev } = await cache.readSnapshot(groupId);
        const delta = await api<{
          rev: number;
          role: GroupRole;
          access: MemberAccess;
          layout: MemberLayout;
          hasOwnLayout: boolean;
          fields: Field[];
          tasks: Task[];
          versions: Version[];
          members: Member[];
        }>(`/api/groups/${groupId}/sync?since=${rev}`);

        await cache.applyDelta(groupId, delta);
        // The server stops sending release history the moment it stops being
        // theirs to see, so a copy cached while they still had access has to go
        // with it rather than lingering in this browser.
        if (!canSee(delta.access, 'releases')) await cache.clearVersions(groupId);
        await get().reloadFromCache();
        set({
          role: delta.role,
          access: delta.access,
          layout: delta.layout,
          hasOwnLayout: delta.hasOwnLayout,
          syncState: 'idle',
          lastSyncedAt: Date.now(),
          syncError: null,
        });
        window.clearTimeout(retryTimer);
      } catch (error) {
        if (error instanceof NetworkError) {
          set({ syncState: 'offline' });
          scheduleRetry(1);
        } else if (error instanceof ApiError && error.status === 401) {
          set({ user: null });
        } else if (error instanceof ApiError && (error.status === 404 || error.status === 403)) {
          // The membership behind this board is gone — removed from the team, or
          // the team itself deleted. Re-reading the list drops the active team,
          // and the route sends the user to one they are still in.
          set({ syncState: 'idle', syncError: 'You no longer have access to that team' });
          await get().loadGroups().catch(() => undefined);
        } else {
          set({
            syncState: 'error',
            syncError: error instanceof Error ? error.message : 'Sync failed',
          });
        }
      }
    },
  };
});

/** Replays one queued operation against the normal REST endpoints. */
async function sendOp(op: OutboxOp): Promise<Task | null> {
  const base = `/api/groups/${op.groupId}/tasks`;

  if (op.kind === 'task.create') {
    const { task } = await api<{ task: Task }>(base, { method: 'POST', body: op.payload });
    return task;
  }

  if (op.kind === 'task.update') {
    const { task } = await api<{ task: Task }>(`${base}/${op.taskId}`, {
      method: 'PATCH',
      body: op.payload,
    });
    return task;
  }

  await api(`${base}/${op.taskId}`, { method: 'DELETE' });
  return null;
}

/**
 * Polls the server so the UI can distinguish "connected" from "offline", and
 * pulls a delta whenever the server has moved ahead of this client.
 */
function startHeartbeat(): void {
  stopHeartbeat();

  const beat = async (): Promise<void> => {
    const { activeGroupId, user } = useStore.getState();
    if (!activeGroupId || !user) return;

    try {
      const { rev } = await api<{ rev: number; serverTime: string }>(
        `/api/groups/${activeGroupId}/heartbeat`,
      );
      const local = await cache.readSnapshot(activeGroupId);
      const { pendingCount, pendingOtherCount } = useStore.getState();

      if (rev > local.rev || pendingCount > 0) {
        await useStore.getState().refresh();
      } else {
        // Nothing to pull for this team, but another team may still be holding
        // unsent work — drain it without disturbing the board on screen.
        if (pendingOtherCount > 0) await useStore.getState().flush();
        useStore.setState({ syncState: 'idle', lastSyncedAt: Date.now() });
      }
    } catch (error) {
      if (error instanceof NetworkError) useStore.setState({ syncState: 'offline' });
    }
  };

  heartbeatTimer = window.setInterval(() => void beat(), HEARTBEAT_MS);

  // The browser's own signals are instant; the poll is the backstop for the
  // case where the machine has a network but the VPS is unreachable.
  window.addEventListener('online', () => void useStore.getState().refresh());
  window.addEventListener('offline', () => useStore.setState({ syncState: 'offline' }));
}

function stopHeartbeat(): void {
  window.clearInterval(heartbeatTimer);
  window.clearTimeout(retryTimer);
}
